import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}`);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, lu: true } : n
      ));
    } catch (err) {
      console.error(err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/mark-all-read');
      setNotifications(notifications.map(n => ({ ...n, lu: true })));
    } catch (err) {
      console.error(err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(notifications.filter(n => n.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  // Déterminer le type et l'icône en fonction du contenu du message
  const getNotificationStyle = (message) => {
    if (!message) return { icon: '🔔', bg: '', border: '' };
    
    const lowerMsg = message.toLowerCase();
    
    if (lowerMsg.includes('suspen')) {
      return { icon: '🚫', bg: 'bg-red-50', border: 'border-l-4 border-red-500' };
    }
    if (lowerMsg.includes('supprim') || lowerMsg.includes('suppression')) {
      return { icon: '🗑️', bg: 'bg-red-50', border: 'border-l-4 border-red-600' };
    }
    if (lowerMsg.includes('avertissement')) {
      return { icon: '⚠️', bg: 'bg-yellow-50', border: 'border-l-4 border-yellow-500' };
    }
    if (lowerMsg.includes('signale') || lowerMsg.includes('signalement')) {
      return { icon: '🚨', bg: 'bg-orange-50', border: 'border-l-4 border-orange-500' };
    }
    if (lowerMsg.includes('accepte') || lowerMsg.includes('acceptée')) {
      return { icon: '✅', bg: 'bg-green-50', border: 'border-l-4 border-green-500' };
    }
    if (lowerMsg.includes('refuse') || lowerMsg.includes('refusée')) {
      return { icon: '❌', bg: 'bg-red-50', border: 'border-l-4 border-red-400' };
    }
    if (lowerMsg.includes('paiement') || lowerMsg.includes('payment')) {
      return { icon: '💰', bg: 'bg-emerald-50', border: 'border-l-4 border-emerald-500' };
    }
    if (lowerMsg.includes('candidature')) {
      return { icon: '📝', bg: 'bg-blue-50', border: 'border-l-4 border-blue-500' };
    }
    
    return { icon: '🔔', bg: '', border: '' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-indigo-700">Notifications</h1>
          {notifications.some(n => !n.lu) && (
            <button
              onClick={markAllAsRead}
              className="px-4 py-2 text-indigo-600 hover:bg-indigo-100 rounded-lg transition"
            >
              Tout marquer comme lu
            </button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">🔔</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucune notification</h3>
            <p className="text-gray-500">Vous êtes à jour !</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map(notif => {
              const style = getNotificationStyle(notif.message);
              return (
                <div
                  key={notif.id}
                  className={`rounded-xl shadow p-5 transition ${style.bg || 'bg-white'} ${
                    !notif.lu ? style.border || 'border-l-4 border-indigo-600' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="text-3xl flex-shrink-0">{style.icon}</div>
                    <div className="flex-1 min-w-0">
                      {/* Message avec sauts de ligne préservés */}
                      <div className={`text-gray-800 whitespace-pre-line leading-relaxed ${!notif.lu ? 'font-medium' : ''}`}>
                        {notif.message}
                      </div>
                      
                      {/* Date et actions */}
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200">
                        <p className="text-sm text-gray-500">
                          {new Date(notif.date_envoi).toLocaleString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        <div className="flex gap-2">
                          {notif.link && (
                            <button
                              onClick={() => {
                                if (!notif.lu) markAsRead(notif.id);
                                navigate(notif.link);
                              }}
                              className="px-3 py-1 text-sm bg-indigo-600 text-white hover:bg-indigo-700 rounded-full transition"
                            >
                              Voir →
                            </button>
                          )}
                          {!notif.lu && (
                            <button
                              onClick={() => markAsRead(notif.id)}
                              className="px-3 py-1 text-sm text-indigo-600 hover:bg-indigo-100 rounded-full transition"
                              title="Marquer comme lu"
                            >
                              ✓ Lu
                            </button>
                          )}
                          <button
                            onClick={() => deleteNotification(notif.id)}
                            className="px-3 py-1 text-sm text-red-600 hover:bg-red-100 rounded-full transition"
                            title="Supprimer"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
